import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getInitials(name: string): string {
  if (!name) return "";

  return name
    .split(" ")
    .map(word => word.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return "agora";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes}m`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks}sem`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths}mes`;
}

/**
 * Calcula a luminosidade de uma cor hexadecimal
 * Retorna um valor entre 0 (preto) e 255 (branco)
 * Fórmula: https://www.w3.org/TR/WCAG20-TECHS/G17.html
 */
function getLuminance(hexColor: string): number {
  // Remove # se existir
  const hex = hexColor.replace('#', '');

  // Converte hex para RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calcula luminosidade relativa (percepção humana)
  return (0.299 * r + 0.587 * g + 0.114 * b);
}

/**
 * Determina se deve usar texto claro ou escuro baseado na cor de fundo
 *
 * @param backgroundColor - Cor de fundo em formato hexadecimal (#RRGGBB)
 * @returns Cor de texto apropriada ('#ffffff' para claro, '#1e293b' para escuro)
 *
 * @example
 * getContrastTextColor('#8b5cf6') // '#ffffff' (roxo escuro → texto claro)
 * getContrastTextColor('#fbbf24') // '#1e293b' (amarelo → texto escuro)
 */
export function getContrastTextColor(backgroundColor: string): string {
  if (!backgroundColor || backgroundColor === 'transparent') {
    return '#1e293b'; // Padrão: texto escuro
  }

  const luminance = getLuminance(backgroundColor);

  // Threshold: 128 (meio do espectro 0-255)
  // Luminosidade < 128 = cor escura → texto claro
  // Luminosidade >= 128 = cor clara → texto escuro
  return luminance < 128 ? '#ffffff' : '#1e293b';
}